import { formatYYYYMMDD } from '@webapp/core/utils/dates/formatYYYYMMDD/formatYYYYMMDD'
import { type CreateTravelRequestFormSchema } from '../../components/TravelRequestForm/TravelRequestForm'
import {
  TravelTripTypes,
  type TripFormValue,
} from '../../components/TravelTripForm/types'
import {
  type CreateTravelRequestDestinationPayload,
  type CreateTravelRequestPayload,
  type TravelDestinationAddressPayload,
} from '../api-models/CreateTravelRequestPayload'

// `PlaceResult.reference` is deprecated upstream (absent from the typings)
// but Angular forwards it and the backend persists it; fall back to
// `place_id`, which the modern Places API documents as its replacement.
const getPlaceReference = (place: google.maps.places.PlaceResult): string => {
  const { reference } = place as { reference?: string }
  return reference ?? place.place_id ?? ''
}

// Port of Angular's `formatPostLocation` (`travel.request.app.js:245-265`):
// the backend validates this shape via `AddressForm` (`io_travel.py`).
const formatPostLocation = (
  place: google.maps.places.PlaceResult
): TravelDestinationAddressPayload => {
  const components = place.address_components ?? []
  const formattedAddress = place.formatted_address ?? place.name ?? ''

  return {
    addressLineOne: formattedAddress,
    city: components[0]?.long_name || components[0]?.short_name || ' ',
    state_province: components[components.length - 2]?.short_name ?? ' ',
    country: components[components.length - 1]?.long_name ?? ' ',
    name: formattedAddress,
    postalCode: 'N/A',
  }
}

const mapTripToDestinationPayload = (
  trip: TripFormValue
): CreateTravelRequestDestinationPayload => {
  const departureDate = formatYYYYMMDD(trip.departureDate.toDate())

  // One-way trips save arrival date = departure date.
  const arrivalDate =
    trip.tripType === TravelTripTypes.RETURN_TRIP && trip.returnDate
      ? formatYYYYMMDD(trip.returnDate.toDate())
      : departureDate

  return {
    Transport: trip.transport,
    departureDate,
    arrivalDate,
    departure_location: trip.departurePlace,
    arrival_location: trip.arrivalPlace,
    departure: trip.departurePlace.place_id ?? '',
    departureAddress: formatPostLocation(trip.departurePlace),
    departureReference: getPlaceReference(trip.departurePlace),
    arrival: trip.arrivalPlace.place_id ?? '',
    arrivalAddress: formatPostLocation(trip.arrivalPlace),
    arrivalReference: getPlaceReference(trip.arrivalPlace),
  }
}

/**
 * Maps the create-form schema + client-side trips to the v1
 * `POST /api/travel/request/` payload: `next_approver` as the approver's
 * user id, `destList` in the Angular API-adapter shape (place ids +
 * `formatPostLocation` addresses + raw `PlaceResult` forwarding) and
 * `YYYY-MM-DD` dates.
 */
export const mapCreateTravelRequestFormToPayload = (
  formValues: CreateTravelRequestFormSchema,
  trips: TripFormValue[]
): CreateTravelRequestPayload => {
  return {
    branch: formValues.location?.id ?? 0,
    department: formValues.department?.id ?? 0,
    next_approver: formValues.nextApprover?.userId ?? null,
    travelBudget: formValues.travelBudget ?? 0,
    purpose: formValues.purpose,
    details: formValues.details,
    destList: trips.map(mapTripToDestinationPayload),
  }
}
